import Card from "@mui/material/Card";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import AccionesSkeleton from "components/SkeletonLoader/AccionesSkeleton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Table from "examples/Tables/Table";
import HistorialAccionesFiltersPanel from "./HistorialAccionesFiltersPanel";

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "context/AuthContext";
import { useUserData } from "context/UserDataContext";
import { useLanguage } from "context/LanguageContext";
import { apiRequest } from "utils/api";
import Cookies from "js-cookie";

// Función para capitalizar nombres y apellidos (primera letra mayúscula, resto minúscula)
const capitalizeName = (str) => {
  if (!str) return "";
  return str.trim().toLowerCase().split(/\s+/).map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/** Incluye acciones del portal empresas; tolera backend sin campo o distinta capitalización */
const esAccionOrigenEmpresas = (accion) => {
  const o = accion?.origen;
  if (o === undefined || o === null || o === "") return true;
  return String(o).trim().toLowerCase() === "empresas";
};

function normalizarListaAccionesApi(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.acciones)) return data.acciones;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}



function Acciones() {
  const [acciones, setAcciones] = useState([]);
  const [usuariosMap, setUsuariosMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [filtroUserId, setFiltroUserId] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const navigate = useNavigate();


  const { user, loading: authLoading } = useAuth();
  const { userData, empresaData, loading: ctxLoading, userDataLoading } = useUserData();
  const { t, lang } = useLanguage();

  const empresaIdResuelto = useMemo(() => {
    const id =
      empresaData?.id ??
      userData?.empresaId ??
      Cookies.get("empresaId") ??
      localStorage.getItem("empresaId");
    return id != null && id !== "" ? String(id) : "";
  }, [empresaData?.id, userData?.empresaId]);

  const esperandoEmpresaEnContexto =
    !authLoading &&
    !empresaIdResuelto &&
    (ctxLoading || userDataLoading);

  useEffect(() => {
    // Si Firebase está cargando, esperar
    if (authLoading) {
      return;
    }

    // Si no hay usuario autenticado, redirigir al login
    if (!user) {
      navigate("/authentication/sign-in");
    }
  }, [navigate, user, authLoading]);


  const fetchAcciones = useCallback(async () => {
    const empresaId =
      empresaData?.id ??
      userData?.empresaId ??
      Cookies.get("empresaId") ??
      localStorage.getItem("empresaId");

    if (!empresaId) {
      console.error("No se encontró empresaId para cargar acciones");
      setAcciones([]);
      setUsuariosMap({});
      return;
    }

    setLoading(true);
    try {
      const response = await apiRequest(`/acciones?empresaId=${empresaId}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error en la respuesta:', response.status, errorText);
        throw new Error('Error al obtener las acciones');
      }

      const data = await response.json();
      const listaCruda = normalizarListaAccionesApi(data);

      if (
        listaCruda.length === 0 &&
        data &&
        typeof data === "object" &&
        data.message &&
        String(data.message).includes("No hay acciones")
      ) {
        setAcciones([]);
        setUsuariosMap({});
        return;
      }

      const accionesPortal = listaCruda.filter(esAccionOrigenEmpresas);
      setAcciones(accionesPortal);

      const userIds = [...new Set(accionesPortal.map((accion) => accion.userId).filter(Boolean))];

      if (userIds.length === 0) {
        setUsuariosMap({});
        return;
      }

      const usuariosPromises = userIds.map(async (userId) => {
        try {
          const resUsuario = await apiRequest(`/datos-usuario-id/${userId}`);
          if (resUsuario.ok) {
            const usuario = await resUsuario.json();
            return { id: userId, usuario };
          }
          return null;
        } catch (error) {
          console.error(`Error obteniendo usuario ${userId}:`, error);
          return null;
        }
      });

      const usuariosData = (await Promise.all(usuariosPromises)).filter((u) => u !== null);
      const nuevoUsuariosMap = {};
      usuariosData.forEach(({ id, usuario }) => {
        if (id && usuario) {
          nuevoUsuariosMap[id] = usuario;
        }
      });
      setUsuariosMap(nuevoUsuariosMap);
    } catch (error) {
      console.error('Error fetching actions:', error);
    } finally {
      setLoading(false);
    }
  }, [empresaData?.id, userData?.empresaId]);

  useEffect(() => {
    if (authLoading) return;
    if (!empresaIdResuelto) {
      if (ctxLoading || userDataLoading) return;
      setAcciones([]);
      setUsuariosMap({});
      return;
    }
    fetchAcciones();
  }, [
    authLoading,
    empresaIdResuelto,
    ctxLoading,
    userDataLoading,
    fetchAcciones,
  ]);

  const tiposAccionUnicos = useMemo(() => {
    const set = new Set(acciones.map((a) => a.accion).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'es'));
  }, [acciones]);

  const funcionariosOpciones = useMemo(() => {
    const ids = [...new Set(acciones.map((a) => a.userId).filter(Boolean))];
    return ids
      .map((id) => {
        const u = usuariosMap[id];
        const nombre = u
          ? `${u.nombre || ''} ${u.apellido || ''}`.trim() || id
          : id;
        return { id, label: capitalizeName(nombre) };
      })
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [acciones, usuariosMap]);

  const accionesFiltradas = useMemo(() => {
    return acciones.filter((a) => {
      if (filtroAccion && a.accion !== filtroAccion) return false;
      if (filtroUserId && String(a.userId) !== String(filtroUserId)) return false;
      if (fechaDesde) {
        const d = new Date(a.fecha);
        const desde = new Date(`${fechaDesde}T00:00:00`);
        if (d < desde) return false;
      }
      if (fechaHasta) {
        const d = new Date(a.fecha);
        const hasta = new Date(`${fechaHasta}T23:59:59.999`);
        if (d > hasta) return false;
      }
      return true;
    });
  }, [acciones, filtroUserId, filtroAccion, fechaDesde, fechaHasta]);

  const columns = [
    { name: "funcionario", align: "left", label: t('history.col.officer'), headerAlign: "left" },
    { name: "accion", align: "left", label: t('history.col.action'), headerAlign: "left" },
    { name: "detalles", align: "left", label: t('history.col.details'), headerAlign: "left" },
    { name: "fecha", align: "left", label: t('history.col.date'), headerAlign: "left" }
  ];

  const rows = accionesFiltradas.map(accion => {
    const usuario = usuariosMap[accion.userId];
    const nombreCompleto = usuario
      ? `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim()
      : t('history.userNotFound');

    return {
      funcionario: (
        <ArgonTypography
          variant="button"
          textTransform="none"
          fontWeight="regular"
          color="#000000"
          sx={{ marginLeft: "20px", display: "block" }}
        >
          {capitalizeName(nombreCompleto)}
        </ArgonTypography>
      ),
      accion: accion.accion,
      detalles: accion.detalles,
      fecha: new Date(accion.fecha).toLocaleString(lang === 'en' ? 'en-US' : 'es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
    };
  });

  return (
    <DashboardLayout>
      <ArgonBox py={3} pb={0}>
        <ArgonBox
          component="button"
          type="button"
          onClick={() => navigate('/ajustes')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            background: 'rgba(255,255,255,0.18)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '10px',
            px: 1.75,
            py: 0.9,
            cursor: 'pointer',
            color: '#fff',
            fontSize: '0.82rem',
            fontWeight: 600,
            letterSpacing: '0.01em',
            transition: 'background 0.18s ease, border-color 0.18s ease',
            '&:hover': {
              background: 'rgba(255,255,255,0.28)',
              borderColor: 'rgba(255,255,255,0.5)',
            },
          }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>←</span>
          {t('settings.title')}
        </ArgonBox>
      </ArgonBox>
      <ArgonBox py={6}>
        <Card
          sx={{
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 16px 40px -12px rgba(52, 71, 103, 0.18)",
            border: "1px solid rgba(233, 236, 239, 0.95)",
            backgroundColor: "#fff",
            WebkitBackfaceVisibility: "hidden",
            backfaceVisibility: "hidden",
          }}
        >
          <ArgonBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              p: { xs: 2.5, sm: 3 },
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              borderBottom: "1px solid rgba(30, 134, 246, 0.08)",
              borderTopLeftRadius: "18px",
              borderTopRightRadius: "18px",
            }}
          >
            <ArgonBox>
              <ArgonTypography
                variant="h5"
                sx={{
                  fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  fontWeight: 700,
                  color: "#344767",
                  letterSpacing: "-0.02em",
                }}
              >
                {t('history.pageTitle')}
              </ArgonTypography>
              <ArgonTypography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  fontSize: "0.8rem",
                  color: "#67748e",
                  fontWeight: 500,
                }}
              >
                {t('history.pageSubtitle')}
              </ArgonTypography>
            </ArgonBox>
            <ArgonBox
              component="button"
              type="button"
              onClick={fetchAcciones}
              disabled={loading || esperandoEmpresaEnContexto || authLoading}
              title="Actualizar lista"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                flexShrink: 0,
                width: 44,
                height: 44,
                border: "none",
                borderRadius: "12px",
                cursor: loading ? "default" : "pointer",
                opacity: loading ? 0.55 : 1,
                bgcolor: "rgba(30, 134, 246, 0.1)",
                color: "#1E86F6",
                transition: "background-color 0.2s ease, transform 0.15s ease",
                "&:hover:not(:disabled)": {
                  bgcolor: "rgba(30, 134, 246, 0.18)",
                },
                "&:active:not(:disabled)": {
                  transform: "scale(0.95)",
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 22 }} />
            </ArgonBox>
          </ArgonBox>
          {!loading && !esperandoEmpresaEnContexto && !authLoading && acciones.length > 0 && (
            <HistorialAccionesFiltersPanel
              filtroUserId={filtroUserId}
              onFiltroUserIdChange={setFiltroUserId}
              filtroAccion={filtroAccion}
              onFiltroAccionChange={setFiltroAccion}
              fechaDesde={fechaDesde}
              onFechaDesdeChange={setFechaDesde}
              fechaHasta={fechaHasta}
              onFechaHastaChange={setFechaHasta}
              funcionariosOpciones={funcionariosOpciones}
              tiposAccionUnicos={tiposAccionUnicos}
            />
          )}
          {loading || esperandoEmpresaEnContexto || authLoading ? (
            <AccionesSkeleton />
          ) : accionesFiltradas.length === 0 && acciones.length > 0 ? (
            <ArgonBox px={3} pb={3} textAlign="center">
              <ArgonTypography variant="body2" sx={{ color: "#67748e", fontWeight: 500 }}>
                {t('history.noMatchingFilters')}
              </ArgonTypography>
            </ArgonBox>
          ) : acciones.length === 0 ? (
            <ArgonBox px={3} pb={3} textAlign="center">
              <ArgonTypography variant="body2" sx={{ color: "#67748e", fontWeight: 500 }}>
                {t('history.noActionsCompany')}
              </ArgonTypography>
            </ArgonBox>
          ) : (
            <Table
              columns={columns.map(col =>
                col.name === "funcionario"
                  ? { ...col, headerStyle: { paddingLeft: "24px" }, style: { paddingLeft: "24px" } }
                  : col
              )}
              rows={rows}
            />
          )}
        </Card>
      </ArgonBox>
    </DashboardLayout>
  );
}

export default Acciones;
